// Free tool pages (/tools/...). The YouTube-link tools hand the link to the app (sign up free, then it
// is waiting in the composer); the chapter checker runs entirely in the browser.
import { faListOl, faFileLines, faWandMagicSparkles, faListCheck } from '@fortawesome/free-solid-svg-icons';

export const TOOLS = [
  {
    slug: 'youtube-timestamp-generator',
    tool: 'timestamps',
    label: 'YouTube timestamp generator',
    icon: faListOl,
    summary: 'Paste a link and get chapters YouTube accepts, starting at 0:00.',
    seo: {
      title: 'Free YouTube timestamp & chapter generator (AI) | Bumpups',
      description: 'Paste a YouTube link and get AI timestamps that work as YouTube chapters: they start at 0:00, stay at least 10 seconds apart and describe each moment. Free, no credit card.',
    },
    h1: 'Free YouTube timestamp generator',
    lead: 'Paste a YouTube link. Bump AI watches the video and writes chapters with specific titles, in the format YouTube turns into chapters.',
    cta: 'Generate timestamps',
    sample: { kind: 'chapters', example: 'procrastination', title: 'Timestamps Bumpups wrote for this TED talk' },
    steps: [
      ['Paste the link', 'Any public YouTube video up to 3 hours.'],
      ['Sign up free', 'One click with Google. Your link is waiting for you.'],
      ['Copy the chapters', 'Paste them into your description. They start at 0:00 and follow YouTube’s rules.'],
    ],
    faq: [
      ['Is it free?', 'Yes. Every account gets 60 free minutes of video each month, with no credit card.'],
      ['Will YouTube show them as chapters?', 'Bumpups timestamps start at 0:00, keep each chapter at least 10 seconds long and stay inside the video, which is what YouTube requires.'],
      ['Can I do videos I haven’t published yet?', 'Yes. Upload the file instead of a link. Your first upload is a free preview, and Pro uploads videos up to 3 hours 30 minutes.'],
      ['My chapters still don’t show. Why?', 'Run them through the free chapter checker. It finds the line that breaks YouTube’s rules and fixes it.'],
    ],
  },
  {
    slug: 'youtube-transcript',
    tool: 'transcript',
    label: 'YouTube transcript generator',
    icon: faFileLines,
    summary: 'Download a YouTube video’s transcript as text, SRT or VTT.',
    seo: {
      title: 'Free YouTube transcript generator: TXT, SRT & VTT | Bumpups',
      description: 'Get the transcript of a YouTube video with timestamps. Download it as text or as SRT and VTT subtitles, and ask questions about the video. Free to start.',
    },
    h1: 'YouTube transcript generator',
    lead: 'Paste a YouTube link and download its transcript with timestamps, as text or as SRT and VTT subtitles. Then ask Bump AI anything about the video.',
    cta: 'Get the transcript',
    sample: {
      kind: 'transcript',
      title: 'The start of a transcript from Bumpups',
      video: { videoId: 'UF8uR6Z6KLc', title: 'Steve Jobs’ 2005 Stanford commencement address' },
      lines: [
        ['0:22', 'Thank You. I am honored to be with you today at your commencement'],
        ['0:30', 'from one of the finest universities in the world.'],
        ['0:35', 'Truth be told I never graduated from college'],
        ['0:41', "and this is the closest I've ever gotten to a college graduation."],
        ['0:47', "Today I want to tell you three stories from my life. That's it."],
        ['0:52', 'No big deal. Just three stories.'],
        ['0:55', 'The first story is about connecting the dots.'],
      ],
    },
    steps: [
      ['Paste the link', 'Any public YouTube video that has captions, or that runs 30 minutes or more.'],
      ['Sign up free', 'One click with Google. Your link is waiting for you.'],
      ['Download it', 'Open Export and choose Transcript (.txt) or Subtitles (.srt or .vtt).'],
    ],
    faq: [
      ['Which formats can I download?', 'Plain text with a timestamp on every line, SRT and WebVTT subtitles, or everything (chapters, chats and transcript) in one Markdown file.'],
      ['What if the video has no captions?', 'Videos of 30 minutes or more are transcribed from the audio. For shorter videos without captions, you still get chapters and can chat with the video.'],
      ['Can I get a transcript of my own video file?', 'Yes. Upload MP4, MOV or WebM files. Your first upload is a free preview, and Pro uploads videos in full.'],
      ['Is it free?', 'Yes. Every account gets 60 free minutes of video each month, with no credit card.'],
    ],
  },
  {
    slug: 'youtube-video-summarizer',
    tool: 'summary',
    label: 'YouTube video summarizer',
    icon: faWandMagicSparkles,
    summary: 'A clear summary of any YouTube video, and answers to your follow-up questions.',
    seo: {
      title: 'Free AI YouTube video summarizer with timestamps | Bumpups',
      description: 'Paste a YouTube link and get a summary of the video, then ask follow-up questions. Bump AI watches the whole video and answers with timestamps. Free to start.',
    },
    h1: 'YouTube video summarizer',
    lead: 'Paste a YouTube link. Bump AI watches the whole video, then summarizes it and answers follow-up questions, pointing to the minute.',
    cta: 'Summarize the video',
    sample: {
      kind: 'summary',
      title: 'A summary Bumpups wrote',
      video: { videoId: 'UF8uR6Z6KLc', title: 'Steve Jobs’ 2005 Stanford commencement address' },
      question: 'Summarize the video.',
      answer: "This video is Steve Jobs' 2005 commencement address at Stanford University. He structures his speech around three stories from his life:\n\n*   **Connecting the Dots:** Jobs recounts dropping out of Reed College, which allowed him to drop in on a calligraphy class. Ten years later, this seemingly impractical knowledge inspired the beautiful typography of the first Macintosh computer. He explains that you can't connect the dots looking forward, only backward, so you must trust that your experiences will eventually connect and lead you down the right path.\n*   **Love and Loss:** He discusses the devastation of being publicly fired from Apple, the company he co-founded, at age 30. However, the \"lightness of being a beginner again\" sparked a highly creative period where he founded NeXT and Pixar, and met his wife. He returned to Apple when they bought NeXT, proving to him that you must find what you love to do and never settle. \n*   **Death:** Jobs shares his recent brush with pancreatic cancer. He explains that remembering you are going to die is the most important tool for making big life choices, as it strips away external expectations, pride, and fear of failure. He urges the graduates not to waste their limited time living someone else's life.\n\nHe concludes his speech by quoting the farewell message from the final issue of the *Whole Earth Catalog*, passing it on as his advice to the graduates: **\"Stay Hungry. Stay Foolish.\"**",
    },
    steps: [
      ['Paste the link', 'Any public YouTube video up to 3 hours.'],
      ['Sign up free', 'One click with Google. Your link is waiting for you.'],
      ['Ask for the summary', 'Then follow up: key takeaways, a quiz, notes, or any question about the video.'],
    ],
    faq: [
      ['Does it watch the video or just read captions?', 'For YouTube videos up to 30 minutes, Bump AI watches the video itself, so it sees what’s on screen too.'],
      ['Can I ask follow-up questions?', 'Yes. The chat remembers the conversation, so you can go deeper, shorten an answer or turn it into notes.'],
      ['Which languages does it answer in?', 'Eleven, including English, Spanish, Portuguese, Hindi, German, French and Japanese.'],
      ['Is it free?', 'Yes. Every account gets 60 free minutes of video each month and 10 chat messages per video.'],
    ],
  },
  {
    slug: 'youtube-chapter-checker',
    tool: 'checker',
    label: 'YouTube chapter checker',
    icon: faListCheck,
    summary: 'Find out why your chapters don’t show, and get a fixed list. No sign-up.',
    seo: {
      title: 'YouTube chapters not showing? Free chapter checker & fixer | Bumpups',
      description: 'Paste your video description’s timestamps to see why YouTube isn’t showing chapters: the 0:00 start, 3 chapters, order and 10-second rules. Get a fixed list instantly. No sign-up.',
    },
    h1: 'YouTube chapters not showing? Check them here',
    lead: 'Paste the timestamps from your description. The checker finds every line that breaks YouTube’s chapter rules and gives you a fixed list to paste back. It runs in your browser, with no sign-up.',
  },
];

export const toolBySlug = slug => TOOLS.find(t => t.slug === slug) || null;
